import { motion } from "framer-motion";
import { ArrowRight, Activity, ShieldCheck, Users, FileStack } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useConfig } from "@/context/ConfigContext";
import { useDemo } from "@/context/DemoContext";
import { scrollToId } from "@/hooks/useLenis";

const STATS = [
  { label: "Response coordination", value: "Real-time", icon: Activity },
  { label: "Evidence integrity", value: "Verified", icon: ShieldCheck },
  { label: "Connected professionals", value: "Unified", icon: Users },
  { label: "AI documentation", value: "Automated", icon: FileStack },
];

export function Flagship() {
  const { config } = useConfig();
  const { launchDemo } = useDemo();
  const carImg = config?.crashready_hero_image;
  const crLogo = config?.crashready_logo_url;

  return (
    <section
      id="platform"
      className="relative overflow-hidden border-y border-white/10 py-28 lg:py-36"
      data-testid="flagship-section"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-[120px]"
        style={{ background: "var(--accent)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-mono text-xs text-white/40">
                02 // FLAGSHIP PLATFORM
              </p>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-6 flex items-center gap-4">
                {crLogo && (
                  <img
                    src={crLogo}
                    alt="Crash Ready"
                    className="h-16 w-16 rounded-xl object-contain"
                    data-testid="crashready-logo"
                  />
                )}
                <h2 className="font-display text-5xl font-light tracking-tight text-white sm:text-6xl">
                  Crash Ready
                  <span className="align-super text-2xl text-white/40">™</span>
                </h2>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-display mt-4 text-xl font-light text-white/80">
                The intelligent accident-response platform.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-body mt-6 max-w-xl leading-relaxed text-white/55">
                Crash Ready brings together clients, attorneys, nurses,
                telehealth providers, drone pilots, AI documentation and
                evidence management into one connected workflow.
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {STATS.map((s) => (
                  <div
                    key={s.label}
                    className="glass rounded-xl p-4"
                    data-testid={`flagship-stat-${s.label.split(" ")[0].toLowerCase()}`}
                  >
                    <s.icon
                      className="h-5 w-5"
                      style={{ color: "var(--accent)" }}
                    />
                    <p className="font-display mt-3 text-lg text-white">
                      {s.value}
                    </p>
                    <p className="font-body text-xs text-white/45">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <button
                  onClick={launchDemo}
                  data-testid="flagship-launch-demo-btn"
                  className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
                  style={{
                    background: "var(--accent)",
                    boxShadow: "0 0 26px var(--accent-glow)",
                  }}
                >
                  Launch Interactive Demo
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => scrollToId("flow")}
                  data-testid="flagship-learn-more-btn"
                  className="inline-flex items-center justify-center rounded-full border border-white/15 px-6 py-3.5 font-medium text-white/90 transition-colors duration-200 hover:border-white/40 hover:bg-white/5"
                >
                  Learn More
                </button>
              </div>
            </Reveal>
          </div>

          {/* Crash Ready F1 showcase */}
          <Reveal delay={0.15}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0C] shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.02] px-4 py-3">
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                  <span className="font-mono ml-3 text-[10px] tracking-[0.2em] text-white/40">
                    CRASH READY — WHEN EVERY SECOND MATTERS
                  </span>
                  <span className="ml-auto flex items-center gap-1.5">
                    <motion.span
                      className="h-1.5 w-1.5 rounded-full bg-red-500"
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                    />
                    <span className="font-mono text-[9px] tracking-[0.2em] text-white/50">
                      LIVE
                    </span>
                  </span>
                </div>

                {/* Cinematic F1 feed */}
                <div className="relative h-[340px] w-full overflow-hidden">
                  {carImg && (
                    <motion.img
                      src={carImg}
                      alt="Crash Ready — precision engineered response"
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                      data-testid="crashready-hero-image"
                      initial={{ scale: 1.02 }}
                      animate={{ scale: [1.02, 1.12, 1.02], x: [0, -18, 0], y: [0, 8, 0] }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  )}

                  {/* moving light sweep */}
                  <motion.div
                    className="pointer-events-none absolute inset-y-0 w-1/2 -skew-x-12"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(0,122,255,0.14), transparent)",
                    }}
                    initial={{ x: "-120%" }}
                    animate={{ x: "260%" }}
                    transition={{
                      duration: 5.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 1.5,
                    }}
                  />

                  {/* scanlines */}
                  <div
                    className="pointer-events-none absolute inset-0 opacity-[0.12]"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 4px)",
                    }}
                  />

                  {/* vignette */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

                  {/* telemetry ticker */}
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-mono text-[9px] tracking-[0.2em] text-white/40">
                          RESPONSE
                        </p>
                        <motion.p
                          className="font-mono text-sm font-bold"
                          style={{ color: "var(--accent)" }}
                          animate={{ opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2.2, repeat: Infinity }}
                        >
                          &lt; 60s
                        </motion.p>
                      </div>
                      <div>
                        <p className="font-mono text-[9px] tracking-[0.2em] text-white/40">
                          STATUS
                        </p>
                        <p className="font-mono text-sm font-bold text-white">
                          ENGAGED
                        </p>
                      </div>
                    </div>
                    <p className="font-mono text-[9px] tracking-[0.2em] text-white/40">
                      CR // TELEMETRY
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="pointer-events-none absolute -inset-4 -z-10 rounded-3xl opacity-30 blur-3xl"
                style={{ background: "var(--accent)" }}
              />
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
